import React, { useState, useEffect } from 'react';
import APIService from '../services/api';
import { avecCle, useCleLecture } from '../services/cleLecture';

/**
 * LE VISAGE D'UN CANDIDAT — un seul composant, partout.
 *
 * Vécu 24.09 : « pourquoi il n'y a ni photo sur les candidats, ni photo sur le
 * tri des profils proposés ? ». La fiche savait afficher une photo, mais ce
 * savoir vivait DANS l'écran Candidats : la liste d'un besoin, la carte de tri
 * et les tiroirs n'en avaient rien. Recopier trente lignes dans chaque écran
 * aurait donné trois comportements de repli différents — ici il n'y en a qu'un.
 *
 * Contrat serveur : seul `photo_preview_url` s'affiche — `photo_url` est un
 * lien d'application du stockage, qui rend une page « Javascript disabled »
 * dans un <img>. La clé est ABSENTE quand la fiche n'a pas de photo : c'est
 * ainsi qu'on sait retomber sur les initiales, sans deviner.
 *
 * `texte` : le repli écrit par l'appelant quand il sait mieux découper le nom
 * (une ligne venue du vivier n'a pas toujours prénom et nom séparés).
 */
/** Le repli qui suit le thème en place, sans que l'appelant ait à le connaître.
 *  (La palette indexée par thème de l'écran Candidats reste chez lui : elle
 *  est SA grammaire, pas celle du composant.) */
export const AV_AUTO = { bg: 'var(--hair)', color: 'var(--text-2)' };

export const initiales = (prenom, nom) =>
  `${(prenom || '').charAt(0)}${(nom || '').charAt(0)}`.toUpperCase() || '?';

const BASE = 'https://besoins-tat-production.up.railway.app';

/** L'adresse servie par notre API, qu'elle arrive absolue ou en chemin relatif. */
export const photoAbs = (url) => {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  if (/^https?:/i.test(u)) return u;
  if (u.startsWith('/')) return `${BASE}${u}`;   // « /candidates/376/photo »
  return null;
};
/** La CLÉ DE LECTURE est apposée à l'ouverture (jamais le jeton de session,
 *  28.09) ; `v` casse le cache après un remplacement, l'adresse de la photo ne
 *  changeant pas. Sans clé encore en main, l'image échoue et le repli
 *  ci-dessous la relit avec l'en-tête. */
export const photoSrc = (url, v) => {
  const abs = photoAbs(url);
  if (!abs) return null;
  const lien = avecCle(abs);
  return v ? `${lien}${lien.includes('?') ? '&' : '?'}v=${v}` : lien;
};

/** Avatar de lecture : photo si le serveur en publie une, initiales sinon (et en repli si l'image casse). */
export default function AvatarCandidat({ cand, url, version, texte = null, av = AV_AUTO, size = '40px', radius = '9px', fontSize = '13px', className = '', style }) {
  const [cassee, setCassee] = useState(false);
  const [blob, setBlob] = useState(null);
  useCleLecture();   // re-rendu quand la clé de lecture arrive ou change
  const u = url !== undefined ? url : cand?.photo_preview_url;
  useEffect(() => { setCassee(false); setBlob(null); }, [u, version]);

  /**
   * Deuxième chance : une balise <img> ne sait porter que le token en query.
   * Si la route ne l'accepte pas (ou répond autre chose qu'une image), la
   * photo était perdue alors qu'elle existe — on la redemande avec l'en-tête
   * Authorization et on l'affiche depuis la mémoire. Sans quoi la fiche montre
   * des initiales juste après un envoi réussi, ce qui donne à croire que
   * l'envoi a échoué.
   */
  useEffect(() => {
    if (!cassee) return undefined;
    const abs = photoAbs(u);
    if (!abs) return undefined;
    let annule = false; let cree = null;
    fetch(abs, { headers: { Authorization: `Bearer ${APIService.getToken()}` } })
      .then(r => ((r.ok && (r.headers.get('content-type') || '').toLowerCase().startsWith('image/')) ? r.blob() : null))
      .then(b => { if (!annule && b) { cree = URL.createObjectURL(b); setBlob(cree); } })
      .catch(() => { /* la photo reste en initiales */ });
    return () => { annule = true; if (cree) URL.revokeObjectURL(cree); };
  }, [cassee, u]);

  const src = blob || (cassee ? null : photoSrc(u, version));
  return (
    <div className={`ats-mono ${className}`} aria-hidden="true"
      style={{ width: size, height: size, borderRadius: radius, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize, fontWeight: 500, flex: 'none', background: av.bg, color: av.color, overflow: 'hidden', ...style }}>
      {src
        ? <img src={src} alt="" onError={() => setCassee(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        : (texte || initiales(cand?.prenom, cand?.nom))}
    </div>
  );
}
